import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, computed, inject, input, viewChild } from '@angular/core';
import { LocationCardSettings } from '@app/features/operating-picture/map-wall-display/models';
import { AerisWeatherIconMapping, WeatherCondition } from '@app/modules/core/weather/weather.model';
import { TranslatePipe } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { LocationWidgetCard } from '../../shared/location-widget-card';
import { TimerService } from '@app/core/services';

@Component({
  selector: 'topo-location-card',
  imports: [TranslatePipe, AsyncPipe, CdkDragHandle],
  templateUrl: './location-card.component.html',
  styleUrl: './location-card.component.scss'
})
export class LocationCardComponent extends LocationWidgetCard<LocationCardSettings> {
  weatherInfo = input<WeatherCondition>();

  showLocationInfo = computed(() => {
    const { showTemperature, showTime, showWeather } = this.settings();
    return showTemperature || showTime || showWeather;
  });

  locationDate = computed(() => {
    if (!this.showLocationInfo()) {
      return null;
    }

    const { isWorld, timezone, utcOffset } = this.location();
    const { worldTimeFormat, timeFormat } = this.settings();

    const topOfMinute$ = this.timer.topOfMinute$;

    if (isWorld) {
      return {
        date$: topOfMinute$.pipe(map(now => now.format(worldTimeFormat)))
      };
    }

    // format without meridiem for ui display
    const format = timeFormat.replace(' A', '');
    const hasMeridiem = timeFormat.indexOf(' A') !== -1;

    if (!Boolean(timezone) && !Boolean(utcOffset)) {
      return null;
    }

    const currentTime$ = topOfMinute$.pipe(map(now => (Boolean(timezone) ? now.tz(timezone) : now.utcOffset(utcOffset))));

    return {
      time$: currentTime$.pipe(map(now => now.format(format).trim())),
      meridiem$: currentTime$.pipe(map(now => (hasMeridiem ? now.format('A') : null)))
    };
  });

  weatherIcon = computed(() => {
    const weather = this.weatherInfo();
    return Boolean(weather) ? `/assets/images/weather/${AerisWeatherIconMapping.get(weather.icon)}.svg` : null;
  });

  temperature = computed(() => {
    const weather = this.weatherInfo();

    if (!Boolean(weather)) {
      return;
    }

    let temperature = weather.tempF;

    if (this.settings()?.tempUnitsCelcius) {
      temperature = ((temperature - 32.0) * 5.0) / 9.0;
    }

    return Math.round(temperature);
  });

  temperatureUnit = computed(() => {
    const weather = this.weatherInfo();

    if (!Boolean(weather)) {
      return;
    }

    return this.settings().tempUnitsCelcius ? 'C' : 'F';
  });
}
